import React, { useEffect } from 'react';
import { ReelProduct } from '../types';
import './ProductTag.css';

interface ProductDrawerProps {
  product: ReelProduct | null;
  isOpen: boolean;
  isMorphing?: boolean;
  onClose: () => void;
}

export const ProductDrawer: React.FC<ProductDrawerProps> = ({
  product,
  isOpen,
  isMorphing = false,
  onClose
}) => {
  const formatPrice = (price: number, currency: string): string => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: currency === 'TRY' ? 'TRY' : currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(price);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!product) return null;

  return (
    <>
      <div 
        className={`product-drawer-backdrop ${isOpen ? 'product-drawer-backdrop--open' : ''}`}
        onClick={handleBackdropClick}
      />
      
      <div className={`product-drawer ${isOpen ? 'product-drawer--open' : ''} ${isMorphing ? 'product-drawer--morphing' : ''}`}>
        <div className="product-drawer__handle">
          <div className="product-drawer__handle-bar" />
        </div>

        <button 
          className="product-drawer__close"
          onClick={onClose}
          aria-label="Kapat"
        >
          <svg 
            width="18" 
            height="18" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="product-drawer__content">
          <div className="product-drawer__image-wrapper">
            <div className="product-drawer__image-container">
              <img
                src={product.imageUrl}
                alt={product.name}
                className="product-drawer__image"
              />
            </div>
          </div>

          <div className="product-drawer__info">
            {product.brand && (
              <span className="product-drawer__brand">{product.brand}</span>
            )}
            <h3 className="product-drawer__name">{product.name}</h3>
            <span className="product-drawer__price">
              {formatPrice(product.price, product.currency)}
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductDrawer;
